import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Home.css';
import {
    AccessTimeOutlined,
    EventAvailableOutlined,
    FormatQuoteRounded,
    GestureOutlined,
    HealingOutlined,
    CheckBoxOutlined,
    LocalOfferOutlined,
    LocationOnOutlined,
    PhoneInTalkOutlined,
    PsychologyAltOutlined,
    SpaOutlined
} from '@mui/icons-material';
import GlobalNavigation from './GlobalNavigation.js';
import FloatingReservationButton from './FloatingReservationButton.js';
import PhoneContactBlock from './PhoneContactBlock.js';
import SeoHelmet from './SeoHelmet.js';
import { docRoutes, lineOfficialUrl, reservationUrl } from './navigationConfig.js';
import ItamiBody from './ItamiBody';
import { getSeoByPath } from '../seoConfig.js';

const heroTreatment = `/img/hero-treatment.jpg`;
const heroTreatmentPc = `/img/hero-treatment-pc.jpg`;
const heroClinic = `/img/hero-clinic.jpg`;
const heroClinicPc = `/img/hero-clinic-pc.jpg`;
const heroMoxa = `/img/hero-moxa.jpg`;
const counselingSession = `/img/counseling-session.jpg`;
const privateReservation = `/img/private-reservation.jpg`;
const rootTreatment = `/img/root-treatment.jpg`;
const aboutNerveTherapy = `/img/about-nerve-therapy.jpg`;
const aboutAcupuncture = `/img/about-acupuncture.jpg`;
const nerveTherapyTrial = `/img/nerve-therapy-trial.webp`;
const facialCare = `/img/facial-care.webp`;
const fullBodyBalance = `/img/full-body-balance.webp`;
const homeSeo = getSeoByPath('/');

const concerns = [
    '慢性的な腰痛があり、なかなか改善しない',
    '眼精疲労や背中・首に違和感がある',
    '病院で異常なしと診断された耳鳴りやめまい',
    '生活の質を上げたい・仕事の効率を上げたい',
    '寝ても休んでも疲れが取れない',
    '家事や仕事がつらくて思うように進まない',
    '良くならない五十肩で腕が上げづらい',
    '肩こりから頭痛が起こりやすい',
    '薬が手放せないほどの頭痛に悩んでいる',
    '病院や整骨院に通っても改善しない'
];

const features = [
    {
        title: '完全予約制のプライベート空間',
        description:
            '一日の施術人数を制限し、お客様同士が顔を合わせない落ち着いた環境をご用意。心身ともにリラックスして施術を受けていただけます。',
        Icon: EventAvailableOutlined,
        image: privateReservation,
        imageAlt: '完全予約制のプライベート空間の写真'
    },
    {
        title: '丁寧なカウンセリング',
        description:
            '東洋医学と西洋医学の両面から、お一人おひとりの状態を丁寧にヒアリング。原因を見極め、最適な施術プランをご提案します。',
        Icon: PsychologyAltOutlined,
        image: counselingSession,
        imageAlt: 'カウンセリングを行う様子'
    },
    {
        title: '根本治療へのこだわり',
        description:
            '神経整体や鍼灸治療などを組み合わせ、神経・筋・関節へアプローチ。原因へ直接働きかけ、施術後の変化と持続力を追求します。',
        Icon: HealingOutlined,
        image: rootTreatment,
        imageAlt: '根本治療の施術風景'
    }
];

const improvementExamples = [
    {
        title: '首・肩',
        details: 'ストレートネック/頭痛/五十肩/手のシビレなど'
    },
    {
        title: '腰・胸郭',
        details: 'ヘルニア/坐骨神経痛/ぎっくり腰/すべり症など'
    },
    {
        title: '足',
        details: '捻挫/靭帯損傷/足底筋膜炎/シンスプリントなど'
    },
    {
        title: '神経系',
        details: '麻痺/自律神経失調症/起立性調節障害など'
    },
    {
        title: '内科系',
        details: '便秘/下痢/生理痛/生理不順/むくみなど'
    },
    {
        title: '産後・美容系',
        details: '姿勢改善/骨盤の歪み改善/産後の痛み・不調など'
    }
];

const acupunctureEffectCategories = [
    {
        category: '婦人科系',
        items: ['生理痛', '月経不順', '不妊', '冷え性']
    },
    {
        category: '消化器系',
        items: ['便秘', '下痢', '消化不良', '胃酸過多']
    },
    {
        category: '呼吸器系',
        items: ['風邪', '気管支炎', '喘息']
    },
    {
        category: '神経系',
        items: ['頭痛', 'めまい', '不眠', '自律神経失調症']
    },
    {
        category: '運動器系',
        items: ['腰痛', 'リウマチ', '五十肩', '外傷の後遺症']
    },
    {
        category: '循環器系',
        items: ['低血圧・高血圧', '動悸', '息切れ']
    }
];

const treatmentHighlights = [
    {
        title: '神経整体',
        description: (
            <>
                <p>神経整体は、神経に優しく働きかけて体を変化させる手技療法です。</p>
                <p>強い刺激ではなく、最小限のソフトな刺激で神経の働きを整え、体を動かしやすくします。</p>
                <p>そのため、子どもから高齢者まで安心して受けられる施術です。</p>
                <div className="treatment-examples__column">
                    <h4>神経整体でお悩みを解消された症例</h4>
                    <ul className="improvement-list">
                        {improvementExamples.map(({ title, details }) => (
                            <li key={title}>
                                <span className="improvement-list__title">◆{title}</span>
                                <span className="improvement-list__details">　{details}</span>
                        </li>
                        ))}
                    </ul>
                    <p className="improvement-list__note">
                        ※神経整体の効果には個人差があり、結果を保証するものではありません。
                        <br />
                        {'　'}症状や体の状態により改善までの時間は異なります。
                        <br />
                        {'　'}まずお体を確認し、一人ひとりに合ったプランをご提案します。
                    </p>
                </div>
            </>
        ),
        Icon: GestureOutlined,
        image: aboutNerveTherapy,
        imageAlt: '神経整体の施術イメージ'
    },
    {
        title: '鍼灸治療',
        description: (
            <>
                <p>
                    鍼や灸による刺激が自律神経や免疫の働きを整え、自然治癒力を高めます。血液・リンパの循環を促し、筋肉の緊張や痛みを和らげる効果が期待できます。
                </p>
                <div className="treatment-examples__column">
                    <h4>鍼灸の有効性が認められる症状</h4>
                    <ul className="effect-list">
                        {acupunctureEffectCategories.map(({ category, items }) => (
                            <li key={category}>
                                <strong>{category}</strong>
                                <span>{items.join(' / ')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </>
        ),
        Icon: SpaOutlined,
        image: aboutAcupuncture,
        imageAlt: '鍼灸治療の施術イメージ'
    }
];

const reviews = [
    {
        name: '八木どんまい',
        date: '2024-03-05',
        comments: [
            '一階が歯科医院なので入口が少し分かりづらかったのですが、それ以外は大満足でした。',
            '施術後は体が軽く、思わず笑顔になるほどの心地よさ。安心して任せられます。'
        ]
    },
    {
        name: 'ここたま',
        date: '2024-01-01',
        comments: [
            '退院後も続いていた慢性的な重だるさが、初回の体験施術で軽くなりました。',
            '鍼灸と神経整体の施術を通して身体の変化を実感し、不安なく通えています。'
        ]
    },
    {
        name: 'i k',
        date: '2023-12-12',
        comments: [
            '腰痛と坐骨神経痛で悩んでいましたが、原因を丁寧に説明していただき安心して施術を受けられました。',
            '施術直後だけでなく、日を追うごとに良くなっていくのが分かり、定期的に通っています。'
        ]
    },
    {
        name: 'Sora Emi',
        date: '2023-12-10',
        comments: [
            '首・肩・背中のこわばりが続き様々な整体を探していたところ、こちらに出会いました。',
            '優しいお人柄と的確な施術で一度でも効果を実感。美容鍼のリフトアップ効果も驚きです。'
        ]
    }
];

const campaigns = [
    {
        title: '神経整体 お試し',
        description: '瞬時に体の変化を引き出す神経整体を初回限定価格で体験。',
        price: <><s><small>7,700円</small></s> → 5,500円</>,
        duration: '目安時間 初回 50分 / 2回目以降 25分',
        Icon: LocalOfferOutlined,
        image: nerveTherapyTrial,
        imageAlt: '神経整体お試しメニューの写真',
        linkTo: docRoutes.campaignNerveTherapy
    },
    {
        title: '小顔施術',
        description: 'リフトアップとフェイスラインの引き締めを目指す美容鍼メニュー。',
        price: <><s><small>19,800円</small></s> → 13,200円</>,
        duration: '目安時間 初回 100分 / 2回目以降 75分',
        Icon: LocalOfferOutlined,
        image: facialCare,
        imageAlt: '小顔施術のイメージ写真',
        linkTo: docRoutes.campaignFacialCare
    },
    {
        title: '全身バランス調整',
        description: '全身の巡りを整え、根本から体調改善を目指すバランス調整。',
        price: <><s><small>11,000円</small></s> → 7,700円</>,
        duration: '目安時間 初回 75分 / 2回目以降 50分',
        Icon: LocalOfferOutlined,
        image: fullBodyBalance,
        imageAlt: '全身バランス調整のイメージ写真',
        linkTo: docRoutes.campaignFullBodyBalance
    }
];

const reviewSliderSettings = {
    dots: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 6000,
    speed: 600,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1
            }
        },
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1
            }
        }
    ]
};

const Home = () => {
    const accessSectionRef = useRef(null);

    const heroSliderSettings = {
        autoplay: true,
        autoplaySpeed: 4500,
        speed: 900,
        fade: true,
        infinite: true,
        arrows: false,
        dots: false,
        pauseOnHover: false,
        pauseOnFocus: false,
        cssEase: 'ease-in-out'
    };

    return (
        <>
            <SeoHelmet {...homeSeo} />
            <div className="home-page">
            <section id="hero" className="hero">
                <div className="hero-bubble hero-bubble--large" />
                <div className="hero-bubble hero-bubble--medium" />
                <div className="hero-bubble hero-bubble--small" />

                <GlobalNavigation includeSections currentRoute="/" showFloatingToggle={false} />

                <div className="hero-content">
                    <div className="hero-visual">
                        <div className="hero-slider-shell">
                            <div className="hero-slider">
                                <Slider {...heroSliderSettings}>
                                    <div>
                                        <div className="hero-visual__panel">
                                            <picture>
                                                <source media="(min-width: 1024px)" srcSet={heroTreatmentPc} />
                                                <img
                                                    src={heroTreatment}
                                                    alt="施術を行う様子"
                                                />
                                            </picture>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="hero-visual__panel">
                                            <picture>
                                                <source media="(min-width: 1024px)" srcSet={heroClinicPc} />
                                                <img
                                                    src={heroClinic}
                                                    alt="院内の写真"
                                                />
                                            </picture>
                                        </div>
                                    </div>
                                </Slider>
                            </div>
                        </div>
                        <div className="hero-visual__catch">
                            <p>本気で治したいと思っている方への<br />施術を行っている鍼灸・整体院です！</p>
                        </div>
                    </div>
                    <div className="hero-note">
                        <p className="hero-note__title">その「痛み」や「不調」諦めないで</p>
                        <div className="hero-note__image">
                            <img
                                src={heroMoxa}
                                alt="お灸"
                            />
                        </div>
                        <p className="hero-note__caption">
                            痛みや不調を我慢していませんか？
                            <br />
                            「何をやってもよくならない」と諦めていませんか？
                            <br />
                            つらい症状には必ず原因があります。
                            <br />
                            原因を取り除き快適な生活を取り戻しましょう。
                            <br />
                            もちろん他の症状もお任せください。
                            <br />
                            当院でしっかり解決しましょう。
                        </p>
                    </div>
                </div>
            </section>

            <div id="body-care" className="home-section-anchor" />
            <ItamiBody />

            <section id="concept" className="info-section concept-section home-section-anchor">
                <div className="section-heading">
                    <span className="section-heading__label">CONCEPT</span>
                    <h2>心と身体のつながりを大切にする治療院</h2>
                    <p>
                        症状の原因を丁寧に見極め、神経整体と鍼灸を中心に根本改善を目指します。安心できる空間で、あなた本来のコンディションを取り戻しましょう。
                    </p>
                </div>
                <div className="concept-intro">
                    <h3>こんなお悩みありませんか？</h3>
                    <ul className="concerns-list">
                        {concerns.map((item) => (
                            <li key={item}>
                                <CheckBoxOutlined className="concerns-list__icon" fontSize="inherit" />
                                <span className="concerns-list__text">{item}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="concept-note">上記のようなお悩みがあれば、ぜひ当院にご相談ください。</p>
                    <div className="section-cta">
                        <Link
                            className="access-actions__button access-actions__button--secondary"
                            to={docRoutes.concept}
                        >
                        コンセプト詳細
                        </Link>
                    </div>
                </div>
            </section>

            <section id="features" className="info-section features-section home-section-anchor">
                <div className="section-heading">
                    <span className="section-heading__label">FEATURES</span>
                    <h2>Takajyuが選ばれる理由</h2>
                </div>
                <div className="feature-grid">
                    {features.map(({ title, description, Icon, image, imageAlt }) => (
                        <article className="feature-card" key={title}>
                            <div className="feature-card__media">
                                <img src={image} alt={imageAlt} loading="lazy" />
                            </div>
                            <div className="feature-card__content">
                                <div className="feature-card__icon">
                                    <Icon fontSize="inherit" />
                                </div>
                                <div>
                                    <h3>{title}</h3>
                                    <p>{description}</p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
                <div className="section-cta">
                    <Link
                        className="access-actions__button access-actions__button--secondary"
                        to={docRoutes.menu}
                    >
                        メニュー
                    </Link>
                </div>
            </section>

            <section id="treatment" className="info-section treatment-section home-section-anchor">
                <div className="section-heading">
                    <span className="section-heading__label">TREATMENT</span>
                    <h2>当院の施術方針</h2>
                    <p>
                        症状の改善を目的に、神経整体と鍼灸を中心とした施術を行います。一人ひとりの状態を見極めながら、優しい刺激で効果的にアプローチします。
                    </p>
                </div>
                <div className="treatment-grid">
                    {treatmentHighlights.map(({ title, description, Icon, image, imageAlt }) => (
                        <article className="treatment-card" key={title}>
                            <div className="treatment-card__media">
                                <img src={image} alt={imageAlt} loading="lazy" />
                            </div>
                            <div className="treatment-card__header">
                                <div className="treatment-card__icon">
                                    <Icon fontSize="inherit" />
                                </div>
                                <h3>{title}</h3>
                            </div>
                            <div className="treatment-card__body">{description}</div>
                        </article>
                    ))}
                </div>
                <div className="section-cta">
                    <Link
                        className="access-actions__button access-actions__button--secondary"
                        to={docRoutes.treatments}
                    >
                        当院の施術
                    </Link>
                </div>
            </section>

            <section id="reviews" className="info-section reviews-section home-section-anchor">
                <div className="section-heading">
                    <span className="section-heading__label">VOICE</span>
                    <h2>お客様の声</h2>
                    <p>嬉しいお言葉を糧に、さらに良い施術体験をご提供してまいります。</p>
                </div>
                <div className="reviews-slider">
                    <Slider {...reviewSliderSettings}>
                        {reviews.map((review) => (
                            <div key={review.name}>
                                <article className="review-card">
                                    <div className="review-card__quote">
                                        <FormatQuoteRounded fontSize="inherit" />
                                    </div>
                                    <div className="review-card__header">
                                        <h3>{review.name}</h3>
                                        <time dateTime={review.date}>{review.date}</time>
                                    </div>
                                    {review.comments.map((comment, index) => (
                                        <p key={index}>{comment}</p>
                                    ))}
                                </article>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>

            <section id="campaign" className="info-section campaign-section home-section-anchor">
                <div className="section-heading">
                    <span className="section-heading__label">CAMPAIGN</span>
                    <h2>初回限定キャンペーン</h2>
                    <p>予告なく終了する場合があります。あらかじめご了承ください。</p>
                </div>
                <div className="campaign-grid">
                    {campaigns.map(({ title, description, price, duration, Icon, image, imageAlt, linkTo }) => (
                        <Link className="campaign-card" key={title} to={linkTo}>
                            <div className="campaign-card__media">
                                <img src={image} alt={imageAlt} loading="lazy" />
                            </div>
                            <div className="campaign-card__header">
                                <div className="campaign-card__icon">
                                    <Icon fontSize="inherit" />
                                </div>
                                <h3>{title}</h3>
                            </div>
                            <p>{description}</p>
                            <div className="campaign-card__meta">
                                <span className="campaign-card__price">{price}</span>
                                <span className="campaign-card__duration">{duration}</span>
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="section-cta">
                    <Link
                        className="access-actions__button access-actions__button--secondary"
                        to={docRoutes.menu}
                    >
                        メニュー
                    </Link>
                </div>
            </section>

            <section id="access" className="info-section access-section home-section-anchor" ref={accessSectionRef}>
                <div className="section-heading">
                    <span className="section-heading__label">INFORMATION</span>
                    <h2>ご予約・お問い合わせ</h2>
                </div>
                <div className="access-grid">
                    <div className="access-card">
                        <h3><AccessTimeOutlined className="heading-icon" />営業時間</h3>
                        <p>
                            月・火・水・金&nbsp; 11:00〜23:00<br />
                            土&nbsp; 12:00〜23:00<br />
                            定休日&nbsp; 日<br />
                            出張日&nbsp; 木
                        </p>
                    </div>
                    <div className="access-card">
                        <h3><PhoneInTalkOutlined className="heading-icon" />ご予約電話番号</h3>
                        <PhoneContactBlock className="access-card__phone-block" />
                    </div>
                    <div className="access-card">
                        <h3><LocationOnOutlined className="heading-icon" />所在地</h3>
                        <p>
                            三田線 春日駅 A5番出口 徒歩1分<br />
                            東京都文京区小石川1-8-7 AKAビル201
                        </p>
                        <p>
                            <a
                                href="https://maps.app.goo.gl/k3LLeGJSvKYq65JR7"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Google Mapsで見る
                            </a>
                        </p>
                    </div>
                    <div className="access-card">
                        <h3>詳細</h3>
                        <p>
                            AKAビル1階が高木歯科になります。<br />
                            歯医者さんの入り口から入って、正面の階段を昇っていただきます。<br />
                            エレベータの隣の201号室になります。
                        </p>
                    </div>
                </div>
                <p className="access-note">LINEでのご予約も承っております。以前のご登録がお済みの方は再登録をお願いいたします。</p>
                <div className="access-actions">
                    <a
                        className="access-actions__button access-actions__button--primary"
                        href={reservationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        ご予約はこちら
                    </a>
                    <a
                        className="access-actions__button access-actions__button--line"
                        href={lineOfficialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        LINEで友達追加
                    </a>
                    <Link
                        className="access-actions__button access-actions__button--secondary"
                        to={docRoutes.reservation}
                    >
                        ご予約・お問い合わせ
                    </Link>
                </div>
            </section>
            <FloatingReservationButton intersectionTargetRef={accessSectionRef} />
        </div>
        </>
    );
};

export { Home };
